import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import T from "../styles/tokens";
import { PFLogo, Ic, Spinner } from "../components/UI";
import * as api from "../utils/api";

const REMOVAL_BULLETS = [
  "All Pathfinder admin guest accounts will be removed from your tenant",
  "The cross-tenant access policy granting Pathfinder access will be deleted",
  "Pathfinder will no longer be able to manage your tenant",
  "Your data and users in your tenant remain unchanged",
  "An audit record of this off-boarding is retained on Pathfinder's side",
];

export default function OffboardingLanding() {
  const { token } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .getOffboardingDetails(token)
      .then(setDetails)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.gray100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: T.gray100, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ background: T.white, borderRadius: 16, padding: 40, maxWidth: 480, textAlign: "center", boxShadow: T.shadowLg }}>
          <div style={{ width: 56, height: 56, borderRadius: "50%", background: T.redBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: T.red, fontSize: 24 }}>✕</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: T.indigo, fontFamily: T.font, margin: "0 0 10px" }}>Link Unavailable</h2>
          <p style={{ fontSize: 14, color: T.charcoal, fontFamily: T.font, lineHeight: 1.6 }}>{error}</p>
          <p style={{ fontSize: 13, color: T.gray400, fontFamily: T.font, marginTop: 20 }}>
            Need help? Contact <a href="mailto:support@pathfindtech.com" style={{ color: T.purple, fontWeight: 600 }}>support@pathfindtech.com</a>
          </p>
        </div>
      </div>
    );
  }

  const consentUrl = api.getOffboardingConsentUrl(token);

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, ${T.indigo} 0%, #7F1D1D 50%, ${T.indigo} 100%)` }}>
      <div className="pf-public-shell" style={{ maxWidth: 640, margin: "0 auto", padding: "60px 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            <PFLogo size={40} />
            <div style={{ textAlign: "left" }}>
              <div style={{ color: T.white, fontSize: 18, fontWeight: 700, fontFamily: T.font }}>Pathfinder Technical Services</div>
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, fontWeight: 500, fontFamily: T.font }}>Managed IT Services</div>
            </div>
          </div>
        </div>

        <div style={{ background: T.white, borderRadius: 16, boxShadow: T.shadowLg, overflow: "hidden" }}>
          <div className="pf-public-card-pad" style={{ background: `linear-gradient(135deg, #B91C1C, #7F1D1D)`, padding: "28px 32px", color: T.white }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Ic.Revoke />
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: T.font }}>Off-boarding Confirmation</span>
            </div>
            <h1 className="pf-public-h1" style={{ fontSize: 22, fontWeight: 700, fontFamily: T.font, margin: 0 }}>
              {details.clientName}, confirm Pathfinder disconnect
            </h1>
            <p style={{ fontSize: 14, opacity: 0.85, fontFamily: T.font, marginTop: 6 }}>
              Pathfinder will be removed from your tenant
            </p>
          </div>

          <div className="pf-public-card-pad" style={{ padding: "28px 32px" }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 14 }}>What will be removed</h3>
            <div style={{ marginBottom: 24 }}>
              {REMOVAL_BULLETS.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: i < REMOVAL_BULLETS.length - 1 ? `1px solid ${T.gray100}` : "none" }}>
                  <span style={{ color: T.red, marginTop: 2, flexShrink: 0, fontWeight: 700 }}>✕</span>
                  <span style={{ fontSize: 14, color: T.charcoal, fontFamily: T.font, lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>

            {details.roles && details.roles.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.indigo, fontFamily: T.font, marginBottom: 10 }}>Admin roles being removed</h3>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {details.roles.map((r) => (
                    <span key={r} style={{ padding: "4px 10px", borderRadius: 4, background: T.redBg, color: T.red, fontSize: 12, fontWeight: 500, fontFamily: T.font }}>{r}</span>
                  ))}
                </div>
              </div>
            )}

            <div style={{ background: T.amberBg, borderRadius: T.radiusSm, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
              <Ic.Clock />
              <span style={{ fontSize: 12, color: T.amber, fontWeight: 600, fontFamily: T.font }}>
                This link expires {new Date(details.expiresAt).toLocaleString()}
              </span>
            </div>

            <a
              href={consentUrl}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                width: "100%", padding: "14px 24px",
                borderRadius: T.radiusSm,
                background: "#B91C1C", color: T.white,
                fontSize: 15, fontWeight: 700, fontFamily: T.font,
                textDecoration: "none",
                boxShadow: "0 4px 16px rgba(185,28,28,0.3)",
              }}
            >
              <Ic.MS /> Confirm &amp; Disconnect
            </a>

            <p style={{ fontSize: 11.5, color: T.gray400, fontFamily: T.font, textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
              You'll be redirected to Microsoft's sign-in page. You must sign in with a Global Administrator account for your organization.
            </p>
          </div>
        </div>

        <p style={{ textAlign: "center", fontSize: 11.5, color: "rgba(255,255,255,0.35)", fontFamily: T.font, marginTop: 24 }}>
          Pathfinder Technical Services • pathfindtech.com
        </p>
      </div>
    </div>
  );
}
